let tokenProvider: (() => Promise<string | null>) | null = null;

export function setTokenProvider(fn: () => Promise<string | null>): void {
  tokenProvider = fn;
}

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly correlationId?: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

interface Envelope<T> {
  data?: T;
  error?: { code: string; message: string; correlationId?: string; details?: unknown };
  correlationId?: string;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = tokenProvider ? await tokenProvider() : null;
  const headers = new Headers(init.headers);
  if (token) headers.set("authorization", `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData) && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }

  const response = await fetch(`${BASE_URL}/api${path}`, { ...init, headers });
  const isJson = response.headers.get("content-type")?.includes("application/json");
  const body: Envelope<T> | undefined = isJson ? await response.json().catch(() => undefined) : undefined;

  if (!response.ok) {
    throw new ApiError(
      response.status,
      body?.error?.code ?? "UNKNOWN_ERROR",
      body?.error?.message ?? `Unerwarteter Fehler (HTTP ${response.status}).`,
      body?.error?.correlationId,
      body?.error?.details,
    );
  }
  return body?.data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, formData: FormData) => request<T>(path, { method: "POST", body: formData }),
};

export async function downloadExport(path: string, fileName: string, body?: unknown): Promise<void> {
  const token = tokenProvider ? await tokenProvider() : null;
  const headers = new Headers();
  if (token) headers.set("authorization", `Bearer ${token}`);
  // Manche Exporte (z.B. das verschluesselte Backup) benoetigen ein vom Nutzer eingegebenes
  // Kennwort - dafuer POST mit JSON-Body statt der sonst reinen GET-Abfrage.
  const init: RequestInit = { headers };
  if (body !== undefined) {
    headers.set("content-type", "application/json");
    init.method = "POST";
    init.body = JSON.stringify(body);
  }
  const response = await fetch(`${BASE_URL}/api${path}`, init);
  if (!response.ok) {
    // Die Serverbegründung anzeigen (z.B. Validierungsfehler am Kennwort) statt nur des Statuscodes.
    const body: Envelope<unknown> | undefined = await response.json().catch(() => undefined);
    const details = body?.error?.details as { message?: string }[] | undefined;
    const reason = (Array.isArray(details) && details[0]?.message) || body?.error?.message;
    throw new Error(reason ? `Export fehlgeschlagen: ${reason}` : `Export fehlgeschlagen (HTTP ${response.status}).`);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  // Im DOM einhängen und die URL erst verzögert freigeben: ein sofortiges revokeObjectURL direkt nach
  // click() bricht den Download in manchen Browsern (Firefox, Safari) ab, bevor er begonnen hat.
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
